import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { sanitizeHref, Url } from '@wix/da-url';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton/BrandedButton';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton/TextButton';
import DescTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/DeviationDescription';
import TextRenderer from '../../TextRenderer';
import CollapsibleBlock from '../../CollapsibleBlock/CollapsibleBlock';
import DeviationDumbView from '../../DeviationViews/Dumbo';
import DeviationLink from '../../Duperbrowse/DeviationLink/DeviationLink';
import { getDeviationUrl } from '../../DeviationViews/_helpers';
import { getIsCurrentUserAuthorOf } from '../../redux/deviations/selectors';
import { formatDollars } from '../../utils/formatting';
import { normalizeOriginalsCompletedOrders } from '../../utils/originalsCompletedOrders';
import { pushAdoptsOrderOriginalDeviationModal } from '../../Modals/redux/actionCreators';

import s from './OriginalsExpandedShopCard.scss';

const MAX_ORDERS_THUMBS = 3;

interface Props {
  deviation: PapiDeviation;
}

export const OriginalsExpandedShopCard: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isOwner = useSelector(state =>
    getIsCurrentUserAuthorOf(state, deviation)
  );

  const dispatchPushAdoptsOrderOriginalDeviationModal = useAction(
    pushAdoptsOrderOriginalDeviationModal
  );
  const handleRequestClick = useCallback(() => {
    dispatchPushAdoptsOrderOriginalDeviationModal(deviation);
  }, [dispatchPushAdoptsOrderOriginalDeviationModal, deviation]);

  const originals = deviation.originals;
  if (!originals) {
    return null;
  }

  const {
    basePrice,
    showInProgressCount,
    inProgressOrdersCount = 0,
    description,
  } = originals;
  const { deviations: completedOrdersDeviations, total: completedOrdersCount } =
    normalizeOriginalsCompletedOrders(originals);

  return (
    <div className={s['root']}>
      <div className={s['thumbs']}>
        <DeviationLink deviation={deviation} key={deviation.deviationId}>
          <DeviationDumbView deviation={deviation} width={355} height={215} />
        </DeviationLink>
        {completedOrdersDeviations.length > 0 && (
          <div className={s['orders']}>
            <div className={s['orders-header']}>
              {t('shop.card.originals.completedOrders')}
              {completedOrdersCount > MAX_ORDERS_THUMBS && (
                <TextButton
                  size="small"
                  variant="main"
                  priority="secondary"
                  href={sanitizeHref(getDeviationUrl(deviation))}
                >
                  {t('shop.card.originals.seeMore')}
                </TextButton>
              )}
            </div>
            <div className={s['orders-thumbs']}>
              {completedOrdersDeviations
                .slice(0, MAX_ORDERS_THUMBS)
                .map(orderDeviation => (
                  <DeviationLink
                    deviation={orderDeviation}
                    key={orderDeviation.deviationId}
                  >
                    <DeviationDumbView
                      key={orderDeviation.deviationId}
                      deviation={orderDeviation}
                      width={113}
                      height={65}
                    />
                  </DeviationLink>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className={s['details']}>
        <div className={s['main-details']}>
          <div className={s['title']}>{deviation.title}</div>

          <div className={s['price-and-total']}>
            <div className={s['price']}>
              {t('shop.card.originals.basePrice')} {formatDollars(basePrice)}
            </div>

            {completedOrdersCount > 0 && (
              <div className={s['total-orders']}>
                {t('shop.thumb.originals.totalOrders', {
                  count: completedOrdersCount,
                })}
              </div>
            )}

            {showInProgressCount && inProgressOrdersCount > 0 && (
              <div className={s['in-progress-orders']}>
                {t('shop.thumb.originals.inProgressOrders', {
                  count: inProgressOrdersCount,
                })}
              </div>
            )}
          </div>
        </div>

        <div className={s['actions']}>
          {!isOwner && (
            <IconButton
              icon={MailIcon}
              href={Url.userMessageLink(deviation.author?.username ?? '')}
              size="large"
              aria-label={t('shop.card.originals.requestButton')}
              variant="originals-ring"
            />
          )}
          {!isOwner && (
            <BrandedButton
              className={s['branded-button']}
              variant="originals"
              size="large"
              onClick={handleRequestClick}
            >
              {t('shop.card.originals.requestButton')}
            </BrandedButton>
          )}
          {isOwner && (
            <BrandedButton
              className={s['branded-button']}
              variant="originals-outline"
              size="large"
              href={Url.studio({
                section: 'orders-originals',
                deviationId: deviation.deviationId,
              })}
            >
              {t('originals.actions.manageOrders')}
            </BrandedButton>
          )}
          {isOwner && (
            <BrandedButton
              className={s['branded-button']}
              variant="originals"
              size="large"
              href={Url.submissionLinkWithId(deviation.deviationId)}
            >
              {t('common.edit')}
            </BrandedButton>
          )}
        </div>

        <div className={s['description']}>
          <CollapsibleBlock
            maxHeight={100}
            buttonTextCollapsed={t('shop.card.originals.readMore')}
          >
            <TextRenderer
              className={s['description-text']}
              textContent={description}
              tipTapViewer={DescTiptapViewer}
            />
          </CollapsibleBlock>
        </div>
      </div>
    </div>
  );
};
OriginalsExpandedShopCard.displayName = 'OriginalsExpandedShopCard';

export default OriginalsExpandedShopCard;
